import {
  Component,
  Element,
  h,
  Host,
  Listen,
  Prop,
  State,
} from "@stencil/core";
import { Cart } from "@apis/model/mini-cart";
import { checkoutUrl } from "./f-cart-flyout-config";
import store, { getCarts } from "@stores/cart.store";
import ContextService from "@globals/helpers/context.service";
import {
  additionalSpacesHeight,
  singleItemHeight,
} from "../../f-flyout/f-flyout-expander/f-flyout-expander-helpers";
import {
  CartFlyoutProps,
  CartUpdatedEventDetails,
} from "./f-cart-flyout-model";

// this component replaces deprecated bcp-mini-cart

@Component({
  tag: "f-cart-flyout",
  shadow: true,
})
export class FCartFlyout {
  @Element() el: HTMLFCartFlyoutElement;

  /**
   * Label shown on the flyout
   */
  @Prop() buttonHiddenLabel: CartFlyoutProps["buttonHiddenLabel"] = "Cart";
  @Prop() buttonTitle: CartFlyoutProps["buttonTitle"] = this.buttonHiddenLabel;
  @Prop() closeButtonHiddenLabel: CartFlyoutProps["closeButtonHiddenLabel"];
  @Prop()
  expanderButtonHiddenLabel: CartFlyoutProps["expanderButtonHiddenLabel"];
  @Prop() cartNameLabel: CartFlyoutProps["cartNameLabel"] = "Cart: ";
  @Prop() totalNetPriceLabel: CartFlyoutProps["totalNetPriceLabel"] =
    "Total net price";
  @Prop() addProductButtonLabel: CartFlyoutProps["addProductButtonLabel"] =
    "Product";
  @Prop() openCartButtonLabel: CartFlyoutProps["openCartButtonLabel"] =
    "Open cart";
  @Prop() titleLabel: CartFlyoutProps["titleLabel"] = "My cart";
  @Prop() quantityLabel: CartFlyoutProps["quantityLabel"] = "quantity: ";
  @Prop() errorMessage: CartFlyoutProps["errorMessage"] = "An Error Occurred";
  @Prop() emptyCartLabel: CartFlyoutProps["emptyCartLabel"] = "Cart is empty";
  @Prop()
  detailedListOfCartsLabel: CartFlyoutProps["detailedListOfCartsLabel"] =
    "Detailed list of carts";
  @Prop() emptyListLabel: CartFlyoutProps["emptyListLabel"] = "Cart is empty";
  @Prop() errorMessageLabel: CartFlyoutProps["errorMessageLabel"] =
    "An error occurred";
  @Prop() numberOfShownProducts: CartFlyoutProps["numberOfShownProducts"] = 3;

  /** Multi cart enabled */
  @Prop() multiCart: CartFlyoutProps["multiCart"] = false;

  /**
   * Specifies the website context path.
   */
  @Prop() bcpContextPath: CartFlyoutProps["bcpContextPath"];

  /**
   * Specifies the path that the user will go after clicking on `Detailed list of carts` button
   */
  @Prop() detailedListOfCartsUrl: CartFlyoutProps["detailedListOfCartsUrl"];

  @Prop() quickAddEnable: CartFlyoutProps["quickAddEnable"] = true;

  @State() recentlyModifiedCart = "";

  @Listen("cartUpdated", { target: "window" })
  async handleAddToCart(
    event: CustomEvent<CartUpdatedEventDetails>
  ): Promise<void> {
    await this.flyout.openFlyout(event.target);
    this.recentlyModifiedCart = event.detail.cartCode;
  }

  private flyout: HTMLFFlyoutWithButtonElement = null;

  async componentWillLoad(): Promise<void> {
    await getCarts();
  }

  private getBadgeCounter = (): number => {
    if (this.multiCart && !ContextService.isAnonymous()) {
      return store.carts?.length ?? 0;
    }
    return store.carts?.[0]?.totalItems ?? 0;
  };

  private handleQuickAddButtonClick = (): void => {
    this.flyout.closeFlyout(false);
  };

  render() {
    const miniCart = {
      "-single": !this.multiCart,
    };
    return (
      <Host class={miniCart} data-test="mini-cart">
        <f-flyout-with-button
          flyoutTitle={`${this.titleLabel} (${this.getBadgeCounter()})`}
          flyoutId="f-cart-flyout"
          ref={(el) => (this.flyout = el)}
          buttonBadgeLabel={this.getBadgeCounter()}
          buttonIconName="shoppingcart"
          buttonTitle={this.buttonTitle}
          closeButtonHiddenLabel={this.closeButtonHiddenLabel}
          flyoutDataTest="cart-flyout"
        >
          <f-visually-hidden>{this.buttonHiddenLabel}</f-visually-hidden>
          {this.quickAddEnable && (
            <f-flyout-feature-bar slot="flyout-feature-bar" variant="group">
              <span
                slot="feature-bar-group-btn"
                class={"mini-cart__feature-bar js-quick-add-btn"}
                data-quick-add-url={`${this.bcpContextPath}quickAdd`}
              >
                <f-button
                  type="button"
                  variant="integrated"
                  onClick={this.handleQuickAddButtonClick}
                >
                  <f-icon iconName="add"></f-icon>
                  {this.addProductButtonLabel}
                </f-button>
              </span>
            </f-flyout-feature-bar>
          )}
          <div slot="flyout-content">
            {/* Multi cart */}
            {store.initialized &&
              !store.loading &&
              this.multiCart &&
              !ContextService.isAnonymous() &&
              store.carts.map(
                ({ entries, totalPrice, guid, code, name }: Cart) => (
                  <f-flyout-expander
                    key={guid}
                    open={code === this.recentlyModifiedCart}
                    showActionButtons={false}
                    contentMaxHeight={
                      (entries?.length || 1) * singleItemHeight +
                      additionalSpacesHeight
                    }
                  >
                    <span slot="flyout-expander-heading">
                      {this.cartNameLabel} {name ?? code}
                    </span>
                    <f-flyout-product-list
                      slot="flyout-expander-content"
                      entries={entries}
                      price={totalPrice}
                      quantityLabel={this.quantityLabel}
                      openDetailsButtonLabel={this.openCartButtonLabel}
                      priceLabel={this.totalNetPriceLabel}
                      emptyListLabel={this.emptyListLabel}
                      detailsUrl={`${checkoutUrl}${code}`}
                      styleIntegrated={true}
                      numberOfItems={this.numberOfShownProducts}
                      expanderButtonHiddenLabel={this.expanderButtonHiddenLabel}
                    />
                  </f-flyout-expander>
                )
              )}
            {/* Single cart */}
            {store.initialized &&
              !store.loading &&
              (ContextService.isAnonymous() || !this.multiCart) && (
                <f-flyout-product-list
                  entries={store.carts?.[0]?.entries}
                  price={store.carts?.[0]?.totalPrice}
                  quantityLabel={this.quantityLabel}
                  openDetailsButtonLabel={this.openCartButtonLabel}
                  priceLabel={this.totalNetPriceLabel}
                  emptyListLabel={this.emptyListLabel}
                  detailsUrl={`${checkoutUrl}${store.carts?.[0]?.code}`}
                  numberOfItems={this.numberOfShownProducts}
                />
              )}
            {store.loading && <f-flyout-loader />}
            {store.is === "ERROR" && (
              <f-flyout-message messageLabel={this.errorMessageLabel} />
            )}
          </div>
          {!ContextService.isAnonymous() && this.multiCart && (
            <f-flyout-footer
              slot="flyout-footer"
              mode="secondary"
              link={this.detailedListOfCartsUrl}
              label={this.detailedListOfCartsLabel}
            />
          )}
        </f-flyout-with-button>
      </Host>
    );
  }
}
